import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserItem, UserSearchResponse } from '../type';
import { Grid, Button, Modal, ModalInner } from 'components';
import { ExternalLinkOutlineIcon, BadgeCheckOutlineIcon, XCircleOutlineIcon } from 'assets/svgs';
import { manageUsersGridColumns } from '../utils'; 
import UserDetailModal from './UserDetailModal';
import axios from 'common/AxiosAuth';

// * Status 스타일 정의
const userStatusStyles = {
  ACTIVE: { title: 'Active', className: 'bg-[#E6FEED] border-[#27B72D] text-[#27B72D]' },
  INACTIVE: { title: 'Inactive', className: 'bg-white border-[#8F8F8F] text-[#8F8F8F]' },
  LOCKED: { title: 'Locked', className: 'bg-[#FEE6E6] border-[#F30808] text-[#F30808]' },
};

interface Props {
  searchValue?: string;
}

const ManageUsersTab = ({ searchValue }: Props) => {
  const navigate = useNavigate();
  const [data, setData] = useState<UserSearchResponse | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [sortField, setSortField] = useState<string>('createdDate');
  const [sortDirection, setSortDirection] = useState<string>('DESC');

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [statusConfirmData, setStatusConfirmData] = useState<any>(null);
  const [showUnlockConfirmModal, setShowUnlockConfirmModal] = useState(false);
  const [unlockConfirmData, setUnlockConfirmData] = useState<any>(null);

  // * 사용자 조회 API
  const getUsers = useCallback(async (showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortField},${sortDirection}`);

      if (searchValue) {
        params.append('keyword', searchValue);
      }

      const { data } = await axios.get(`/api/admin/member/search?${params.toString()}`);

      // * API 응답
      const responseData = data?.data || data || {};
      const content = responseData.content || [];
      const totalPages = responseData.totalPages || 1;
      const totalElements = responseData.totalElements || 0;

      // * 페이징 정보 설정
      setTotalPages(totalPages);
      setTotalElements(totalElements);
      setData(responseData);
    } catch (error) {
      console.error('회원 조회 실패:', error);
      // * 에러 시 빈 데이터
      setData({
        content: [],
        totalPages: 1,
        totalElements: 0,
        pageable: {
          pageNumber: 0,
          pageSize: 10,
          sort: { empty: true, sorted: false, unsorted: true },
          offset: 0,
          paged: true,
          unpaged: false
        },
        last: true,
        size: 10,
        number: 0,
        sort: { empty: true, sorted: false, unsorted: true },
        first: true,
        numberOfElements: 0,
        empty: true
      });
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [currentPage, searchValue, sortField, sortDirection]);

  const handleClickDetail = async (user: UserItem) => {
    try {
      // * 상세조회 API 호출
      const { data } = await axios.get(`/api/admin/member/${user.memberId}`);
      
      setSelectedUser(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('회원 상세조회 실패:', error);
      alert('사용자 상세정보를 불러오는데 실패했습니다.');
    }
  };

  const handleClickReservedSchedules = (user: UserItem) => {
    // * Manage Schedule 페이지로 이동하면서 email을 쿼리 파라미터로 전달
    // * 페이지 이동 후 검색어를 미리 설정하기 위해 sessionStorage 사용
    sessionStorage.setItem('scheduleSearchKeyword', user.email);
    sessionStorage.setItem('scheduleAutoSearch', 'true');
    // * 깜빡임 방지
    navigate(`/manage-schedule?keyword=${encodeURIComponent(user.email)}&page=0&size=10&sort=scheduleId,desc`, { replace: true });
  };

  const handleClickPaymentHistory = (user: UserItem) => {
    // * Manage Payment 페이지로 이동하면서 email을 쿼리 파라미터로 전달
    // * 페이지 이동 후 검색어를 미리 설정하기 위해 sessionStorage 사용
    sessionStorage.setItem('paymentSearchKeyword', user.email);
    sessionStorage.setItem('paymentAutoSearch', 'true');
    // * 깜빡임 방지
    // TODO : 추후 검색 API 붙이면 수정
    navigate(`/manage-payment?keyword=${encodeURIComponent(user.email)}`, { replace: true });
  };

  const handleToggleStatus = () => {
    if (!selectedUser) return;
    
    // * 확인 모달 표시
    setStatusConfirmData(selectedUser);
    setShowStatusConfirmModal(true);
    setShowDetailModal(false);
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirmData) return;
    
    try {
      // * 회원 탈퇴/복구 API 호출
      await axios.patch(`/api/admin/member/toggle-active/${statusConfirmData.memberId}`);
      // * 성공 시 새로고침
      getUsers();
      setShowStatusConfirmModal(false);
      setStatusConfirmData(null);
    } catch (error) {
      console.error('회원 탈퇴/복구 실패:', error);
      alert('회원 탈퇴/복구에 실패했습니다.');
    }
  };

  const handleStatusCancel = () => {
    setShowStatusConfirmModal(false);
    setStatusConfirmData(null);
  };

  const handleUnlock = () => {
    if (!selectedUser) return;
    
    // * 확인 모달 표시
    setUnlockConfirmData(selectedUser);
    setShowUnlockConfirmModal(true);
    setShowDetailModal(false);
  };

  const handleUnlockConfirm = async () => {
    if (!unlockConfirmData) return;
    
    try {
      // * 회원 잠금 해제 API 호출
      await axios.patch(`/api/admin/member/unlock/${unlockConfirmData.memberId}`);
      // * 성공 시 새로고침
      getUsers();
      setShowUnlockConfirmModal(false);
      setUnlockConfirmData(null);
    } catch (error) {
      console.error('잠금 해제 실패:', error);
      alert('잠금 해제에 실패했습니다.');
    }
  };

  const handleUnlockCancel = () => {
    setShowUnlockConfirmModal(false);
    setUnlockConfirmData(null);
  };

  // * currentPage 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, sortField, sortDirection]);

  useEffect(() => {
    // * 초기 로딩
    const showLoading = isInitialLoad || data === null;
    getUsers(showLoading);
  }, [getUsers, isInitialLoad]);

  if (!data) return null;

  return (
    <>
      <Grid
        className="h-full max-w-full"
        gridColumns={manageUsersGridColumns}
        gridData={data?.content || []}
        searchValue={searchValue}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        options={{
          useFilter: false, 
          useCheckbox: true,
          useSort: {
            use: true,
            sortKeys: ['name', 'email', 'loginMethod', 'emailVerified', 'status', 'role']
          },
          usePagination: {
            use: true,
            maxPage: totalPages
          },
          customCell: {
            emailVerified: (_, row) => (
              <span>{row.emailVerified ? <BadgeCheckOutlineIcon className="w-6 h-6 [&_path]:stroke-green-600" /> : <XCircleOutlineIcon className="w-6 h-6 [&_path]:stroke-red-500" />}</span>
            ),
            status: (value, row) => {
              const user = row as UserItem;
              const status = userStatusStyles[user.status as keyof typeof userStatusStyles] || userStatusStyles.INACTIVE;
              
              return (
                <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                  <span className="text-nowrap text-small font-regular">{status.title}</span>
                </div>
              )
            },
            reservedSchedules: (_, row) => {
              const user = row as UserItem;
              return (
                <div 
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  onClick={() => handleClickReservedSchedules(user)}
                >
                  <ExternalLinkOutlineIcon />
                </div>
              );
            },
            paymentHistory: (_, row) => {
              const user = row as UserItem;
              return (
                <div 
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  onClick={() => handleClickPaymentHistory(user)}
                >
                  <ExternalLinkOutlineIcon />
                </div>
              );
            },
            actions: (_, row) => {
              const user = row as UserItem;
              return (
                <Button
                  text="View Details"
                  type="secondary"
                  size="small"
                  onClick={() => handleClickDetail(user)}
                />
              );
            },
          }
        }}
      />
      
      <UserDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        user={selectedUser}
        onToggleStatus={handleToggleStatus}
        onUnlock={handleUnlock}
      />
      
      {/* 탈퇴/복구 변경 확인 모달 */}
      <Modal isOpened={showStatusConfirmModal}>
        <ModalInner className="md:w-[400px]" title="Warning" onClickClose={handleStatusCancel}>
          <p className="p-4 pt-0 text-body1">
            {statusConfirmData?.status === 'Inactive' 
              ? `Are you sure you want to activate ` 
              : `Are you sure you want to deactivate `
            }
            <span className="font-bold">{statusConfirmData?.email}</span>?
          </p>
          <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
            <Button className="h-9" text="Cancel" onClick={handleStatusCancel} type="secondary" size="large" />
            <Button 
              className={`h-9 ${statusConfirmData?.status === 'Inactive' ? '' : 'bg-red-600 border-red-600 hover:bg-red-700 text-white'}`}
              text={statusConfirmData?.status === 'Inactive' ? 'Activate' : 'Deactivate'} 
              onClick={handleStatusConfirm} 
              type={statusConfirmData?.status === 'Inactive' ? 'blue' : 'secondary'}
              size="large" 
            />
          </div>
        </ModalInner>
      </Modal>

      {/* 잠금 해제 확인 모달 */}
      <Modal isOpened={showUnlockConfirmModal}>
        <ModalInner className="md:w-[400px]" title="Warning" onClickClose={handleUnlockCancel}>
          <p className="p-4 pt-0 text-body1">
            Are you sure you want to unlock <span className="font-bold">{unlockConfirmData?.email}</span>?
          </p>
          <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
            <Button className="h-9" text="Cancel" onClick={handleUnlockCancel} type="secondary" size="large" />
            <Button 
              className="h-9"
              text="Unlock" 
              onClick={handleUnlockConfirm} 
              type="blue"
              size="large" 
            />
          </div>
        </ModalInner>
      </Modal>
    </>
  );
};

export default ManageUsersTab;
